import {
  BadgeDollarSign,
  Check,
  CircleAlert,
  ExternalLink,
  Loader2,
  Ticket,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  captureCueboardObservabilityEvent,
  captureCueboardStepEvent,
} from '@/utils/posthog';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  createWebsiteBillingPortalSession,
  createWebsiteCheckoutSession,
  getWebsiteBillingSummary,
  getWebsiteBillingPlans,
  refreshWebsiteCredits,
  redeemWebsiteCode,
  type WebsiteBillingPlan,
  type WebsiteCheckoutMode,
  type WebsiteCredits,
} from '../lib/auth';
import { formatCredits, formatPlanName } from '../lib/billing-format';

function getErrorStatus(error: unknown): number | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }

  return undefined;
}

function getErrorRequestId(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'requestId' in error &&
    typeof error.requestId === 'string'
  ) {
    return error.requestId;
  }

  return undefined;
}

function formatCurrency(amountCents: number, currency: string) {
  if (amountCents === 0) {
    return 'Free';
  }

  const normalizedCurrency = currency ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

function formatPeriod(period: string) {
  switch (period.toLowerCase()) {
    case 'monthly':
      return 'month';
    case 'annual':
    case 'yearly':
      return 'year';
    case 'once':
      return 'one-time';
    default:
      return period;
  }
}

function formatStatus(status?: string | null) {
  if (!status) {
    return 'No subscription';
  }

  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
}

function formatNextReset(value?: string | null) {
  const parsed = parseDate(value);
  if (!parsed) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

function parseDate(value?: string | null) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function subscriptionCheckoutDisabledReason(credits: WebsiteCredits | null) {
  if (credits?.sub_status?.toLowerCase() !== 'active') {
    return null;
  }
  if (!credits?.sub_cancel_at) {
    return null;
  }

  const periodEnd = parseDate(credits.sub_current_period_end);
  if (periodEnd && periodEnd.getTime() <= Date.now()) {
    return null;
  }

  const periodEndLabel = formatNextReset(credits.sub_current_period_end);
  if (periodEndLabel) {
    return `Your current subscription remains active until ${periodEndLabel}. You can choose a new plan after it ends.`;
  }

  return 'Your current subscription is still active. You can choose a new plan after it ends.';
}

function getCheckoutMode(plan: WebsiteBillingPlan): WebsiteCheckoutMode | null {
  switch (plan.type.toLowerCase()) {
    case 'subscription':
      return 'subscription';
    case 'addon':
      return 'payment';
    default:
      return null;
  }
}

function sortPlans(plans: WebsiteBillingPlan[]) {
  return [...plans].sort((left, right) => {
    const amountDiff = left.amount_cents - right.amount_cents;
    if (amountDiff !== 0) {
      return amountDiff;
    }

    return left.credits_grant - right.credits_grant;
  });
}

function planDescription(plan: WebsiteBillingPlan) {
  if (plan.display_copy?.trim()) {
    return plan.display_copy.trim();
  }

  if (plan.type.toLowerCase() === 'addon') {
    return 'One-time credits that land immediately after checkout.';
  }

  return `${formatCredits(plan.credits_grant)} credits per ${formatPeriod(plan.period)}.`;
}

function Banner({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'error';
  children: ReactNode;
}) {
  const toneClassName =
    tone === 'success'
      ? 'border-emerald-200/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
      : tone === 'warning'
        ? 'border-amber-200/70 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300'
        : 'border-rose-200/70 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300';
  const Icon = tone === 'success' ? Check : CircleAlert;

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-xl border px-4 py-3 text-sm',
        toneClassName
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function BillingPlanCard({
  plan,
  currentPlanCode,
  checkoutPlanCode,
  checkoutDisabledReason,
  onCheckout,
}: {
  plan: WebsiteBillingPlan;
  currentPlanCode?: string | null;
  checkoutPlanCode?: string | null;
  checkoutDisabledReason?: string | null;
  onCheckout: (plan: WebsiteBillingPlan) => Promise<void>;
}) {
  const mode = getCheckoutMode(plan);
  const isCurrent =
    currentPlanCode?.toLowerCase() === plan.plan_code.toLowerCase();
  const isOpeningCheckout = checkoutPlanCode === plan.plan_code;
  const isCheckoutDisabled = Boolean(checkoutDisabledReason);
  const ctaLabel =
    plan.type.toLowerCase() === 'addon' ? 'Buy add-on' : 'Choose plan';

  return (
    <Card className={cn(isCurrent && 'border-primary/50 shadow-md')}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{formatPlanName(plan.plan_code)}</CardTitle>
            <CardDescription className="mt-1">
              {planDescription(plan)}
            </CardDescription>
          </div>
          <div className="rounded-full border border-border/70 bg-background px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {plan.type}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-foreground">
              {formatCurrency(plan.amount_cents, plan.currency)}
            </div>
            <div className="text-sm text-muted-foreground">
              {plan.type.toLowerCase() === 'addon'
                ? 'One-time purchase'
                : `Per ${formatPeriod(plan.period)}`}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-foreground">
              {formatCredits(plan.credits_grant)}
            </div>
            <div className="text-sm text-muted-foreground">credits</div>
          </div>
        </div>

        <Button
          className="w-full"
          variant={isCurrent ? 'outline' : 'default'}
          disabled={
            !mode || isCurrent || isOpeningCheckout || isCheckoutDisabled
          }
          onClick={() => {
            void onCheckout(plan);
          }}
        >
          {isOpeningCheckout ? (
            <Loader2 className="size-4 animate-spin" />
          ) : plan.type.toLowerCase() === 'addon' ? (
            <BadgeDollarSign className="size-4" />
          ) : null}
          {isCurrent
            ? 'Current plan'
            : isOpeningCheckout
              ? 'Opening checkout…'
              : isCheckoutDisabled
                ? 'Available later'
                : ctaLabel}
        </Button>
        {checkoutDisabledReason ? (
          <p className="text-sm text-muted-foreground">
            {checkoutDisabledReason}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

type BillingPageProps = {
  section: 'credits' | 'plans' | 'redeem';
};

export function BillingPage({ section }: BillingPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redeemInputRef = useRef<HTMLInputElement | null>(null);

  const [credits, setCredits] = useState<WebsiteCredits | null>(null);
  const [plans, setPlans] = useState<WebsiteBillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [checkoutPlanCode, setCheckoutPlanCode] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const redirectToLogin = useCallback(() => {
    const next = `${location.pathname}${location.search}`;
    navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const loadPage = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      setError('');

      try {
        if (silent) {
          await refreshWebsiteCredits();
        }
        const [plansResponse, creditsResponse] = await Promise.all([
          getWebsiteBillingPlans(),
          getWebsiteBillingSummary(),
        ]);
        setPlans(plansResponse.plans);
        setCredits(creditsResponse);
      } catch (loadError) {
        if (getErrorStatus(loadError) === 401) {
          redirectToLogin();
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load billing'
        );
      } finally {
        setLoading(false);
      }
    },
    [redirectToLogin]
  );

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (loading || searchParams.get('redeem') !== '1') {
      return;
    }

    const input = redeemInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    input.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }, [loading, searchParams]);

  const checkoutBanner = useMemo(() => {
    const checkoutState = searchParams.get('checkout');
    if (checkoutState === 'success') {
      return {
        tone: 'success' as const,
        message:
          'Checkout completed. Your latest credits will appear here after refresh.',
      };
    }

    if (checkoutState === 'cancelled') {
      return {
        tone: 'warning' as const,
        message: 'Checkout was canceled. You can restart it anytime.',
      };
    }

    return null;
  }, [searchParams]);

  const purchasablePlans = useMemo(
    () => plans.filter(plan => getCheckoutMode(plan) !== null),
    [plans]
  );
  const addonPlans = useMemo(
    () =>
      sortPlans(
        purchasablePlans.filter(plan => plan.type.toLowerCase() === 'addon')
      ),
    [purchasablePlans]
  );
  const subscriptionPlans = useMemo(
    () =>
      sortPlans(
        purchasablePlans.filter(
          plan => plan.type.toLowerCase() === 'subscription'
        )
      ),
    [purchasablePlans]
  );

  const currentPlanCode = credits?.plan_code ?? null;
  const currentPlan = plans.find(
    plan => plan.plan_code.toLowerCase() === currentPlanCode?.toLowerCase()
  );
  const nextResetLabel = formatNextReset(credits?.next_reset_at);
  const subscriptionDisabledReason =
    subscriptionCheckoutDisabledReason(credits);

  const handleCheckout = useCallback(
    async (plan: WebsiteBillingPlan) => {
      const mode = getCheckoutMode(plan);
      if (!mode) {
        setError('Checkout is unavailable for this plan.');
        return;
      }
      if (
        plan.type.toLowerCase() === 'subscription' &&
        subscriptionDisabledReason
      ) {
        setNotice(subscriptionDisabledReason);
        return;
      }

      setCheckoutPlanCode(plan.plan_code);
      setError('');
      setNotice('');
      captureCueboardStepEvent({
        stepName: 'billing.checkout',
        stepState: 'started',
        surface: 'website_billing',
        properties: {
          plan_code: plan.plan_code,
          plan_type: plan.type,
        },
      });

      try {
        const successUrl = new URL(
          '/settings/billing/plans?checkout=success',
          window.location.origin
        ).toString();
        const cancelUrl = new URL(
          '/settings/billing/plans?checkout=cancelled',
          window.location.origin
        ).toString();
        const response = await createWebsiteCheckoutSession({
          lookup_key: plan.plan_code,
          mode,
          success_url: successUrl,
          cancel_url: cancelUrl,
        });
        captureCueboardStepEvent({
          stepName: 'billing.checkout',
          stepState: 'created',
          surface: 'website_billing',
          properties: {
            plan_code: plan.plan_code,
            plan_type: plan.type,
          },
        });
        window.location.assign(response.checkout_url);
      } catch (checkoutError) {
        if (getErrorStatus(checkoutError) === 401) {
          redirectToLogin();
          return;
        }
        captureCueboardObservabilityEvent({
          failureClass: 'billing.preflight_error',
          severity: 'critical',
          surface: 'website_billing',
          requestId: getErrorRequestId(checkoutError),
          statusCode: getErrorStatus(checkoutError),
          error: checkoutError,
          diagnosisHint:
            'Checkout session creation failed in the website billing path.',
          properties: {
            plan_code: plan.plan_code,
            plan_type: plan.type,
            suspect_layer: 'billing',
          },
        });

        setError(
          checkoutError instanceof Error
            ? checkoutError.message
            : 'Failed to open checkout'
        );
      } finally {
        setCheckoutPlanCode(null);
      }
    },
    [redirectToLogin, subscriptionDisabledReason]
  );

  const handleOpenPortal = useCallback(async () => {
    setOpeningPortal(true);
    setError('');
    setNotice('');

    try {
      const returnUrl = new URL(
        '/settings/billing/credits',
        window.location.origin
      ).toString();
      const response = await createWebsiteBillingPortalSession(returnUrl);
      window.location.assign(response.url);
    } catch (portalError) {
      if (getErrorStatus(portalError) === 401) {
        redirectToLogin();
        return;
      }

      setError(
        portalError instanceof Error
          ? portalError.message
          : 'Failed to open billing portal'
      );
    } finally {
      setOpeningPortal(false);
    }
  }, [redirectToLogin]);

  const handleRedeem = useCallback(async () => {
    const nextCode = redeemCode.trim();
    if (!nextCode) {
      return;
    }

    setRedeeming(true);
    setError('');
    setNotice('');

    try {
      const response = await redeemWebsiteCode(nextCode);
      setRedeemCode('');
      await loadPage({ silent: true });
      setNotice(
        `Redeemed ${formatCredits(response.credits_granted)} credits from ${formatPlanName(response.plan_code)}. Balance is now ${formatCredits(response.balance_after)}.`
      );
    } catch (redeemError) {
      if (getErrorStatus(redeemError) === 401) {
        redirectToLogin();
        return;
      }

      setError(
        redeemError instanceof Error
          ? redeemError.message
          : 'Failed to redeem code'
      );
    } finally {
      setRedeeming(false);
    }
  }, [loadPage, redeemCode, redirectToLogin]);

  let sectionContent: ReactNode;
  switch (section) {
    case 'credits':
      sectionContent = (
        <section className="max-w-2xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-xl font-semibold text-foreground">
              Current balance
            </h2>
            <div className="rounded-full border border-border/70 bg-background px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {formatStatus(credits?.sub_status)}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading your billing details…
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <div className="text-sm text-muted-foreground">Active plan</div>
                <div className="mt-1 text-2xl font-semibold text-foreground">
                  {currentPlan
                    ? formatPlanName(currentPlan.plan_code)
                    : currentPlanCode
                      ? formatPlanName(currentPlanCode)
                      : 'Free'}
                </div>
              </div>

              <div className="grid border-y border-border/70 sm:grid-cols-2 sm:divide-x sm:divide-y-0 divide-y divide-border/70">
                <div className="py-4 sm:pr-6">
                  <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Available credits
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">
                    {formatCredits(credits?.available_credits ?? 0)}
                  </div>
                </div>

                <div className="py-4 sm:pl-6">
                  <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Next reset
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">
                    {nextResetLabel || '—'}
                  </div>
                </div>
              </div>

              {credits?.sub_status === 'active' ? (
                <Button
                  variant="outline"
                  disabled={openingPortal}
                  onClick={() => {
                    void handleOpenPortal();
                  }}
                >
                  {openingPortal ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ExternalLink className="size-4" />
                  )}
                  Manage subscription
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Use Plans to buy add-ons or change your subscription.
                </p>
              )}
            </div>
          )}
        </section>
      );
      break;
    case 'redeem':
      sectionContent = (
        <section className="max-w-xl space-y-5">
          <div className="flex items-start gap-3">
            <Ticket className="mt-0.5 size-5 text-muted-foreground" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Redeem code
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Apply a code from your team, promo, or campaign.
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Redeemed credits land in your balance immediately when the code is
            valid.
          </p>

          <div className="space-y-2">
            <label
              htmlFor="billing-redeem-code"
              className="text-sm font-medium text-foreground"
            >
              Redeem code
            </label>
            <Input
              ref={redeemInputRef}
              id="billing-redeem-code"
              value={redeemCode}
              autoComplete="off"
              placeholder="Enter your redeem code"
              disabled={redeeming}
              onChange={event => {
                setRedeemCode(event.target.value);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleRedeem();
                }
              }}
            />
          </div>

          <Button
            className="w-full"
            disabled={redeeming || redeemCode.trim().length === 0}
            onClick={() => {
              void handleRedeem();
            }}
          >
            {redeeming ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Ticket className="size-4" />
            )}
            Redeem code
          </Button>
        </section>
      );
      break;
    case 'plans':
      sectionContent = (
        <>
          {addonPlans.length > 0 ? (
            <section className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Top up credits
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  One-time add-ons for immediate credit balance increases.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {addonPlans.map(plan => (
                  <BillingPlanCard
                    key={plan.plan_code}
                    plan={plan}
                    currentPlanCode={currentPlanCode}
                    checkoutPlanCode={checkoutPlanCode}
                    onCheckout={handleCheckout}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {subscriptionPlans.length > 0 ? (
            <section className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Subscription plans
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Recurring plans refresh credits on their billing schedule.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {subscriptionPlans.map(plan => (
                  <BillingPlanCard
                    key={plan.plan_code}
                    plan={plan}
                    currentPlanCode={currentPlanCode}
                    checkoutPlanCode={checkoutPlanCode}
                    checkoutDisabledReason={subscriptionDisabledReason}
                    onCheckout={handleCheckout}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {!loading &&
          addonPlans.length === 0 &&
          subscriptionPlans.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
              Billing plans have not been configured yet.
            </div>
          ) : null}
        </>
      );
      break;
  }

  return (
    <div className="flex flex-col gap-6">
      {notice ? <Banner tone="success">{notice}</Banner> : null}
      {section === 'plans' && checkoutBanner ? (
        <Banner tone={checkoutBanner.tone}>{checkoutBanner.message}</Banner>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}

      {sectionContent}
    </div>
  );
}
